import { toast } from 'react-toastify';

export const errorHandler = (error) => {
  let message;
  if (error.response && error.response.data) {
    message = error.response.data.detail || JSON.stringify(error.response.data);
  } else if (error.message) {
    message = `Error: ${error.message}`;
  } else {
    message = 'An unknown error occurred.';
  }
  toast.error(message);
  return message;
};


// utils/extractErrorMessage.js (or inline)
export function extractErrorMessage(err) {
  const raw =
    err?.response?.data?.error ??
    err?.response?.data ??
    err?.error ??
    err?.message ??
    err;

  if (raw == null) return 'Unknown error';

  // Helper to flatten DRF-style error objects into "field: message" strings
  const flattenObjectErrors = (obj) => {
    const parts = [];
    for (const [field, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        parts.push(`${field}: ${value.map(v => String(v)).join('; ')}`);
      } else if (value && typeof value === 'object') {
        // Nested objects (rare) - stringify to keep field label
        parts.push(`${field}: ${JSON.stringify(value)}`);
      } else if (typeof value === 'string') {
        parts.push(`${field}: ${value}`);
      }
    }
    return parts.join(', ');
  };

  // 1) If we already have an object, flatten it first (preserves field names)
  if (raw && typeof raw === 'object') {
    // Prefer nested "error" object if present
    const target =
      (raw.error && typeof raw.error === 'object' ? raw.error : null) ?? raw;
    const result = flattenObjectErrors(target);
    if (result) return result;
  }

  // Convert whatever we have into text
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);

  // 2) Try to normalize Python/DRF strings to JSON and parse, then flatten with fields
  const normalized = text
    // Replace ErrorDetail(string='...') with just the message "...", keeping quotes for JSON
    .replace(/ErrorDetail\(string='([^']+)'[^)]*\)/g, '"$1"')
    // Convert single quotes to double quotes to mimic valid JSON
    .replace(/'/g, '"');

  try {
    const parsed = JSON.parse(normalized);
    const target =
      (parsed?.error && typeof parsed.error === 'object'
        ? parsed.error
        : parsed);
    if (typeof target === 'string') return target;
    if (target && typeof target === 'object') {
      const result = flattenObjectErrors(target);
      if (result) return result;
    }
  } catch {
    // Ignore and fall through
  }

  // 3) As a last resort, extract ErrorDetail messages without fields
  const detailMatches = [...text.matchAll(/ErrorDetail\(string='([^']+)'[^)]*\)/g)];
  if (detailMatches.length) {
    return detailMatches.map((m) => m[1]).join(', ');
  }

  // 4) Final fallback
  return String(raw);
}